import {
  Injectable,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom, filter, timeout, of } from 'rxjs';
import { environment } from 'environments/environment';

import { FeatureFlag } from '../enums/feature-flag.enum';

interface FeatureFlagsResponse {
  flags: Record<string, boolean>;
  atualizadoEm: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly API_URL = `${environment.apiUrl}/feature-flags`;
  private readonly POLLING_INTERVAL_MS = 2 * 60 * 1000;
  private readonly TIMEOUT_CARREGAMENTO_MS = 5000;

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly flags = signal<Record<string, boolean>>({});
  private readonly carregado = signal(false);
  private readonly ultimaAtualizacao = signal<Date | null>(null);
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  private readonly carregado$ = toObservable(this.carregado);

  constructor() {
    this.carregarFlags();
    this.iniciarPolling();
  }

  estaHabilitado(flagName: FeatureFlag | string): boolean {
    return this.flags()[flagName] === true;
  }

  estaHabilitado$(flagName: FeatureFlag | string) {
    return computed(() => this.flags()[flagName] === true);
  }

  obterTodasFlags() {
    return computed(() => this.flags());
  }

  foiCarregado(): boolean {
    return this.carregado();
  }

  obterUltimaAtualizacao() {
    return computed(() => this.ultimaAtualizacao());
  }

  async aguardarCarregamento(): Promise<boolean> {
    if (this.carregado()) {
      return true;
    }

    return firstValueFrom(
      this.carregado$.pipe(
        filter(carregado => carregado === true),
        timeout({
          each: this.TIMEOUT_CARREGAMENTO_MS,
          with: () => of(true),
        }),
      ),
    );
  }

  private carregarFlags(): void {
    this.http
      .get<FeatureFlagsResponse>(this.API_URL)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.flags.set(response.flags);
          this.carregado.set(true);
          this.ultimaAtualizacao.set(new Date(response.atualizadoEm));
        },
        error: () => {
          this.carregado.set(true);
        },
      });
  }

  recarregar(): void {
    this.carregarFlags();
  }

  private iniciarPolling(): void {
    this.pollingInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.carregarFlags();
      }
    }, this.POLLING_INTERVAL_MS);

    this.destroyRef.onDestroy(() => {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    });
  }
}
